import { CATALOG_ANCESTOR_FILTERS, type CatalogPage } from './catalog-navigation.js'
import { catalogSortTerms, type CatalogSortTerm } from './catalog-sorting.js'
export interface TutorPopularity { callCount: number; share: number; relativeToTop: number }
export interface PopularityWindow { start: string; end: string }
export const TUTOR_POPULARITY_THRESHOLD = 0.8
export const TUTOR_POPULARITY_HELP = `Started regular calls in the last 30 days; diagnostics excluded. Relative score: coach calls ÷ most-used coach calls; the top coach is 100%. Share is the percentage of all such calls. Both are independent of list filters. Popular marks coaches with at least ${TUTOR_POPULARITY_THRESHOLD * 100}% of the top coach’s calls.`
export interface CatalogRef { id: string; label: string }
export interface CatalogBlocker extends CatalogRef { type: 'program' | 'theme' }
export interface CatalogRow extends CatalogRef {
  contentKey: string
  titleJa: string
  status: 'draft' | 'active' | 'retired' | null
  inheritedStatus: 'draft' | 'active' | 'retired' | null
  available: boolean
  availabilityBlocker: CatalogBlocker | null
  revisionIssue: 'unusable_active_revision' | null
  kind: 'lesson' | 'diagnostic' | null
  program: CatalogRef | null
  level: CatalogRef | null
  theme: CatalogRef | null
  position: number | null
  childCount: number
  scenarioCount?: number | null
  diagnostic: CatalogRef | null
  programs: CatalogRef[]
  minimumCefrLevel: string | null
  maximumCefrLevel: string | null
  popularity?: TutorPopularity | null
}
export interface CatalogScope { program: CatalogRef | null; level: CatalogRef | null; theme: CatalogRef | null; valid: boolean }
export interface CatalogResult { items: CatalogRow[]; total: number; page: number; limit: number; scope: CatalogScope; popularityWindow?: PopularityWindow | null }
export function tutorLevelAccess(minimum: string | null, maximum: string | null): string {
  if (!minimum && !maximum) return 'All levels'
  if (minimum && maximum) return minimum === maximum ? `${minimum} only` : `${minimum}–${maximum}`
  return minimum ? `${minimum} and above` : `${maximum} and below`
}
export interface CatalogListParams {
  q?: string; program?: string; level?: string; theme?: string; id?: string
  kind?: 'lesson' | 'diagnostic'; sort?: CatalogSortTerm[]; page?: number; limit?: number
  status?: 'draft' | 'active' | 'retired'; availability?: 'available' | 'unavailable'
}
export function catalogListParams(page: CatalogPage, params: URLSearchParams): CatalogListParams {
  const number = Number(params.get('page'))
  const ancestor = (key: string) => CATALOG_ANCESTOR_FILTERS[page].includes(key) ? params.get(key) || undefined : undefined
  const status = params.get('status')
  const availability = params.get('availability')
  return {
    q: params.get('q') || undefined,
    program: ancestor('program'), level: ancestor('level'), theme: ancestor('theme'),
    kind: page === 'scenarios' ? (params.get('kind') || undefined) as CatalogListParams['kind'] : undefined,
    status: ['draft', 'active', 'retired'].includes(status ?? '') ? status as CatalogListParams['status'] : undefined,
    availability: page !== 'tutors' && ['available', 'unavailable'].includes(availability ?? '') ? availability as CatalogListParams['availability'] : undefined,
    sort: catalogSortTerms(page, params),
    page: Number.isSafeInteger(number) && number > 0 && number <= 1_000_000 ? number : 1,
    limit: 25,
  }
}

export const CATALOG_SEARCH_PLACEHOLDERS: Record<CatalogPage, string> = {
  programs: 'Program name',
  levels: 'Level or Program name',
  themes: 'Theme title (English or Japanese)',
  scenarios: 'Scenario title (English or Japanese) or position',
  tutors: 'Tutor name (English or Japanese)',
}
